import { CSSProperties, useEffect } from 'react';
import { CountdownPosition } from '@/types';
import { TimerTemplate } from '@lib/timerTemplates';
import { COUNTDOWN_POSITION_CLASS } from '@lib/countdownPosition';
import { loadGoogleFont, resolveFontFamily, toCssFontFamily } from '@lib/fontFamily';

type TimerTemplatePreviewProps = {
	template: TimerTemplate;
	countdownPosition: CountdownPosition;
	isWide: boolean;
};

const STATIC_TIME_UNITS = [
	{ value: '01', label: 'Days' },
	{ value: '11', label: 'Hours' },
	{ value: '59', label: 'Minutes' },
	{ value: '59', label: 'Seconds' },
];

export default function TimerTemplatePreview({ template, countdownPosition, isWide }: TimerTemplatePreviewProps) {
	const titleTimerClass = `title-timer-container ${COUNTDOWN_POSITION_CLASS[countdownPosition]}`;
	const fontFamilyName = resolveFontFamily(template.patch.font);
	const fontFamily = toCssFontFamily(fontFamilyName);
	const textAlignmentClass = 'text-alignment-left';

	useEffect(() => {
		loadGoogleFont(fontFamilyName);
	}, [fontFamilyName]);

	return (
		<div
			className={`timer-template-preview timer-template-preview-${template.id} ${
				isWide ? 'timer-template-preview-wide' : 'timer-template-preview-tall'
			} flex h-full w-full items-center justify-center overflow-hidden rounded-[22px] bg-cover bg-center px-12 py-8 max-md:px-9 max-md:py-4`}
			style={{
				backgroundColor: template.patch.background_color ?? '#0C1839',
				backgroundImage: template.defaultBackgroundName
					? `url(/api/public/default_backgrounds/${encodeURIComponent(template.defaultBackgroundName)})`
					: undefined,
				color: template.patch.time_units_color ?? '#A1B2D9',
				fontFamily,
			}}
		>
			<div className={titleTimerClass}>
				<div className={`title-subtitle-container ${textAlignmentClass}`}>
					{template.patch.title && (
						<div className='title line-clamp-2' style={buildFontStyles(template.patch.title_font_style)}>
							{template.patch.title}
						</div>
					)}
					{template.patch.subtitle && (
						<div className='subtitle line-clamp-2' style={buildFontStyles(template.patch.subtitle_font_style)}>
							{template.patch.subtitle}
						</div>
					)}
				</div>
				<div className='timer-countdown'>
					{STATIC_TIME_UNITS.map((unit, index) => (
						<div className='time-unit' key={unit.label}>
							<div className='label' style={index < STATIC_TIME_UNITS.length - 1 ? { paddingRight: '8px' } : undefined}>
								{unit.label}
							</div>
							<div className='number-wrapper'>
								<div
									className='number'
									style={{
										color: template.patch.countdown_color ?? '#D1FF53',
										background: template.patch.timer_window_color ?? '#172B61',
									}}
								>
									{unit.value}
								</div>
								{index < STATIC_TIME_UNITS.length - 1 && (
									<div className='colon' style={{ color: template.patch.time_units_color ?? '#A1B2D9' }}>
										:
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function buildFontStyles(styleString: string | undefined): CSSProperties {
	if (!styleString) {
		return {};
	}

	const [, fontWeightStyle, color] = styleString.split(' ');

	return {
		color,
		fontWeight: fontWeightStyle?.includes('bold') ? 700 : 400,
		fontStyle: fontWeightStyle?.includes('italic') ? 'italic' : 'normal',
	};
}
